'use client';

/**
 * PriceBookItemPicker — add one-time items from the published price book to an
 * invoice: implementation and integration fees, custom-feature packages,
 * prepaid hours, migration tiers and add-ons, at their published prices (and
 * the sponsored-cohort price where the book has one).
 */

import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function headers(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}

export interface PriceBookPick {
  code: string;
  name: string;
  description: string;
  unit_price: number;
  line_type: 'one_time' | 'service';
}

interface CatalogItem {
  id: string; item_type: string; code: string; name: string; client_label: string | null;
  pricing: Record<string, any>; book_code: string; book_version: number;
}

const TYPES = 'one_time,service_package,migration_tier,add_on';
const GROUP: Record<string, string> = {
  one_time: 'One-time fees', service_package: 'Custom work', migration_tier: 'Migration', add_on: 'Add-ons',
};
const price = (p: Record<string, any>) => Number(p.amount ?? p.unit_price ?? 0);

export default function PriceBookItemPicker({ onPick, onClose }: { onPick: (item: PriceBookPick) => void; onClose: () => void }) {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops/price-books/catalog?types=${TYPES}`, { headers: headers() })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d?.detail || `Request failed (${r.status})`);
        setItems(d.items || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const pick = (it: CatalogItem, cohort = false) => {
    const amount = cohort ? Number(it.pricing.cohort_amount) : price(it.pricing);
    onPick({
      code: it.code,
      name: it.name + (cohort ? ' (sponsored cohort)' : ''),
      description: it.client_label || '',
      unit_price: amount,
      line_type: it.pricing.unit ? 'service' : 'one_time',
    });
  };

  const groups = items.reduce<Record<string, CatalogItem[]>>((acc, it) => { (acc[it.item_type] ||= []).push(it); return acc; }, {});

  return (
    <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 dark:border-indigo-800 dark:bg-indigo-950/20">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
          Price book{items[0] ? ` · ${items[0].book_code} v${items[0].book_version}` : ''}
        </span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
      {loading ? (
        <div className="flex items-center text-xs text-gray-500"><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Loading…</div>
      ) : error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-500">No published price book items.</p>
      ) : (
        <div className="max-h-72 space-y-3 overflow-y-auto">
          {Object.entries(GROUP).filter(([t]) => groups[t]?.length).map(([t, label]) => (
            <div key={t}>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
              {groups[t].map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-2 rounded px-2 py-1 text-sm hover:bg-white dark:hover:bg-gray-800">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900 dark:text-gray-100">{it.name}</div>
                    <div className="truncate text-xs text-gray-500">{it.client_label}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-xs">
                    <button onClick={() => pick(it)} className="rounded bg-white px-2 py-1 font-medium text-indigo-700 shadow-sm hover:bg-indigo-50 dark:bg-gray-800">
                      Add ${price(it.pricing).toLocaleString()}{it.pricing.unit ? `/${it.pricing.unit}` : ''}
                    </button>
                    {it.pricing.cohort_amount != null && (
                      <button onClick={() => pick(it, true)} className="rounded bg-white px-2 py-1 text-gray-600 shadow-sm hover:bg-gray-50 dark:bg-gray-800">
                        Cohort ${Number(it.pricing.cohort_amount).toLocaleString()}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
